import type { IFilters } from "../../../interfaces/StockList.interface";

export interface SortOption {
  value: string;
  label: string;
}

export interface AEMSortOptions {
  amountHighToLow?: string;
  amountLowToHigh?: string;
  alphabeticallyAscending?: string;
  alphabeticallyDescending?: string;
}

export function buildSortOptions(aemSortOptions?: AEMSortOptions): SortOption[] {
  return [
    {
      value: "amount_high_to_low",
      label: aemSortOptions?.amountHighToLow ?? "Amount - high to low",
    },
    {
      value: "amount_low_to_high",
      label: aemSortOptions?.amountLowToHigh ?? "Amount - low to high",
    },
    {
      value: "asec",
      label: aemSortOptions?.alphabeticallyAscending ?? "Alphabetically - ascending",
    },
    {
      value: "desc",
      label: aemSortOptions?.alphabeticallyDescending ?? "Alphabetically - descending",
    },
  ];
}

export type StockListingSearchParamValue = string | string[] | null | undefined;

export type StockListingSearchParamRecord = Record<
  string,
  StockListingSearchParamValue
>;

export type StockFilterCategoryKey =
  | "alphabetical"
  | "exchange"
  | "sector"
  | "mktCapType";

export const createDefaultStockFilters = (): IFilters => ({
  index: [],
  sector: [],
  mktcap: [],
  alphabetic: [],
});

const STOCK_FILTER_CATEGORY_MAP: Record<StockFilterCategoryKey, keyof IFilters> = {
  alphabetical: "alphabetic",
  exchange: "index",
  sector: "sector",
  mktCapType: "mktcap",
};

const STOCK_URL_FILTER_KEYS: Record<keyof IFilters, string> = {
  index: "exchange",
  sector: "sector",
  alphabetic: "alphabetical",
  mktcap: "mktCapType",
};

const getMultiValues = (params: URLSearchParams, ...keys: string[]): string[] => {
  const values: string[] = [];
  for (const key of keys) {
    const keyValues = params
      .getAll(key)
      .flatMap((value) => value.split(","))
      .map((value) => value.trim())
      .filter(Boolean);
    values.push(...keyValues);
  }
  return values;
};

export function mapStockFilterCategoryToFilterKey(
  categoryKey: string,
): keyof IFilters | null {
  return (
    STOCK_FILTER_CATEGORY_MAP[
      categoryKey as StockFilterCategoryKey
    ] ?? null
  );
}

export function isStockFilterSelected(list: unknown[], value: string): boolean {
  return list.some((item) => String(item) === value);
}

export function normalizeStockIndexValues(values: unknown[]): number[] {
  return values
    .map((value) => Number(value))
    .filter((value) => !Number.isNaN(value));
}

export function normalizeStockFiltersForRequest(filters: IFilters): IFilters {
  return {
    index: normalizeStockIndexValues(filters.index ?? []),
    sector: (filters.sector ?? []).map(String).filter(Boolean),
    mktcap: (filters.mktcap ?? []).map(String).filter(Boolean),
    alphabetic: (filters.alphabetic ?? []).map(String).filter(Boolean),
  };
}

export function serializeStockFilters(filters: IFilters): string {
  return JSON.stringify(normalizeStockFiltersForRequest(filters));
}

export function areStockFiltersEqual(a: IFilters, b: IFilters): boolean {
  return serializeStockFilters(a) === serializeStockFilters(b);
}

export function getStockFilterSelectionCount(filters: IFilters): number {
  return (
    (filters.alphabetic?.length ?? 0) +
    (filters.index?.length ?? 0) +
    (filters.sector?.length ?? 0) +
    (filters.mktcap?.length ?? 0)
  );
}

export function parseStockFiltersFromSearchParams(params: URLSearchParams): IFilters {
  return {
    index: normalizeStockIndexValues(
      getMultiValues(params, STOCK_URL_FILTER_KEYS.index, "index"),
    ),
    sector: getMultiValues(params, STOCK_URL_FILTER_KEYS.sector),
    alphabetic: getMultiValues(
      params,
      STOCK_URL_FILTER_KEYS.alphabetic,
      "alphabetic",
    ),
    mktcap: getMultiValues(params, STOCK_URL_FILTER_KEYS.mktcap, "mktcap"),
  };
}

export function buildSearchParamsFromRecord(
  record: StockListingSearchParamRecord,
): URLSearchParams {
  const params = new URLSearchParams();

  for (const [key, raw] of Object.entries(record)) {
    if (raw == null) {
      continue;
    }
    if (Array.isArray(raw)) {
      for (const value of raw) {
        if (value != null) {
          params.append(key, String(value));
        }
      }
      continue;
    }
    params.append(key, String(raw));
  }

  return params;
}

export function parseStockFiltersFromSearchParamRecord(
  record: StockListingSearchParamRecord,
): IFilters {
  return parseStockFiltersFromSearchParams(buildSearchParamsFromRecord(record));
}

export function updateSearchParamsWithStockFilters(
  baseParams: URLSearchParams,
  filters: IFilters,
): URLSearchParams {
  const params = new URLSearchParams(baseParams.toString());
  params.delete(STOCK_URL_FILTER_KEYS.index);
  params.delete(STOCK_URL_FILTER_KEYS.sector);
  params.delete(STOCK_URL_FILTER_KEYS.alphabetic);
  params.delete(STOCK_URL_FILTER_KEYS.mktcap);

  const normalized = normalizeStockFiltersForRequest(filters);

  for (const value of normalized.index) {
    params.append(STOCK_URL_FILTER_KEYS.index, String(value));
  }

  for (const value of normalized.sector) {
    params.append(STOCK_URL_FILTER_KEYS.sector, String(value));
  }

  for (const value of normalized.alphabetic) {
    params.append(STOCK_URL_FILTER_KEYS.alphabetic, String(value));
  }

  for (const value of normalized.mktcap) {
    params.append(STOCK_URL_FILTER_KEYS.mktcap, String(value));
  }

  return params;
}

export function toggleStockFilterValue(
  filters: IFilters,
  categoryKey: string,
  optionValue: string,
  normalizeIndex = false,
): IFilters {
  const filterKey = mapStockFilterCategoryToFilterKey(categoryKey);
  if (!filterKey) {
    return filters;
  }

  const previousValues = (filters[filterKey] ?? []).map(String);

  if (categoryKey === "alphabetical") {
    const isAlreadySelected =
      previousValues[0] === optionValue && previousValues.length === 1;

    return {
      ...filters,
      alphabetic: isAlreadySelected ? [] : [optionValue],
    };
  }

  const nextValues = previousValues.includes(optionValue)
    ? previousValues.filter((value) => value !== optionValue)
    : [...previousValues, optionValue];

  if (filterKey === "index" && normalizeIndex) {
    return {
      ...filters,
      index: normalizeStockIndexValues(nextValues),
    };
  }

  return {
    ...filters,
    [filterKey]: nextValues,
  };
}

export function normalizeQueryStringEncoding(queryString: string): string {
  // URLSearchParams serializes spaces as "+"; our URL format expects "%20".
  return queryString.replace(/\+/g, "%20");
}

type StockListingHeaderInput = {
  title?: string | null;
  headerText?: string | null;
};

export function resolveStockListingHeaderContent({
  tableContent,
  fallbackTitle = null,
  fallbackHeaderText = null,
}: {
  tableContent?: StockListingHeaderInput | null;
  fallbackTitle?: string | null;
  fallbackHeaderText?: string | null;
}): {
  title: string | null;
  headerText: string | null;
  shouldRenderHeader: boolean;
  shouldRenderHeaderBar: boolean;
} {
  const safeTableContent =
    tableContent && typeof tableContent === "object" ? tableContent : undefined;

  const hasAemTitle = Boolean(
    safeTableContent &&
      Object.prototype.hasOwnProperty.call(safeTableContent, "title"),
  );

  const hasAemHeaderText = Boolean(
    safeTableContent &&
      Object.prototype.hasOwnProperty.call(safeTableContent, "headerText"),
  );

  const title = hasAemTitle
    ? (safeTableContent?.title ?? fallbackTitle)
    : fallbackTitle;

  const headerText = hasAemHeaderText
    ? (safeTableContent?.headerText ?? fallbackHeaderText)
    : fallbackHeaderText;

  const shouldRenderHeader = hasAemTitle
    ? safeTableContent?.title !== undefined &&
      safeTableContent?.title !== null
    : Boolean(title);

  const shouldRenderHeaderBar = hasAemHeaderText
    ? safeTableContent?.headerText !== undefined &&
      safeTableContent?.headerText !== null
    : Boolean(headerText);

  return {
    title,
    headerText,
    shouldRenderHeader,
    shouldRenderHeaderBar,
  };
}

export function getSingleQueryParamValue(
  value: StockListingSearchParamValue,
): string | undefined {
  if (Array.isArray(value)) {
    const firstNonEmpty = value.find(
      (item) => typeof item === "string" && item.trim().length > 0,
    );
    return firstNonEmpty?.trim();
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }

  return undefined;
}
